/*
  <collection-card-dropdown>
  A styled dropdown used on collection product cards. It wraps a hidden native
  <select> (kept in sync) so the existing <collection-card-variants> controller
  keeps working — selecting an option updates the <select> and dispatches a
  bubbling `change` event the controller already listens for.
*/
if (!customElements.get('collection-card-dropdown')) {
  class CollectionCardDropdown extends HTMLElement {
    connectedCallback() {
      this.select = this.querySelector('.cc-dropdown__native');
      this.trigger = this.querySelector('[data-cc-trigger]');
      this.label = this.querySelector('[data-cc-label]');
      this.menu = this.querySelector('[data-cc-menu]');
      this.options = Array.from(this.querySelectorAll('.cc-dropdown__option'));

      if (!this.select || !this.trigger || !this.menu) return;

      this._onTriggerClick = (event) => {
        event.stopPropagation();
        this.toggle();
      };
      this._onMenuClick = (event) => {
        const option = event.target.closest('.cc-dropdown__option');
        if (!option) return;
        this.selectValue(option.dataset.ccValue);
        this.close();
        this.trigger.focus();
      };
      this._onDocClick = (event) => {
        if (!this.contains(event.target)) this.close();
      };
      this._onKeydown = (event) => {
        if (event.key === 'Escape' && !this.menu.hidden) {
          this.close();
          this.trigger.focus();
        }
      };
      // Re-sync the visible UI if the selection is changed elsewhere.
      this._onSelectChange = () => this.syncFromSelect();

      this.trigger.addEventListener('click', this._onTriggerClick);
      this.menu.addEventListener('click', this._onMenuClick);
      document.addEventListener('click', this._onDocClick);
      document.addEventListener('keydown', this._onKeydown);
      this.select.addEventListener('change', this._onSelectChange);
    }

    disconnectedCallback() {
      document.removeEventListener('click', this._onDocClick);
      document.removeEventListener('keydown', this._onKeydown);
    }

    toggle() {
      if (this.menu.hidden) this.open();
      else this.close();
    }

    open() {
      this.menu.hidden = false;
      this.trigger.setAttribute('aria-expanded', 'true');
    }

    close() {
      this.menu.hidden = true;
      this.trigger.setAttribute('aria-expanded', 'false');
    }

    selectValue(value) {
      if (value == null) return;
      this.select.value = value;
      // Notify the <collection-card-variants> controller (image/price/variant id).
      this.select.dispatchEvent(new Event('change', { bubbles: true }));
      this.syncFromSelect();
    }

    syncFromSelect() {
      const value = this.select.value;
      const selectedOption = this.select.selectedOptions[0];
      if (this.label && selectedOption) {
        this.label.textContent = selectedOption.dataset.optionValue || value;
      }
      this.options.forEach((option) => {
        const isActive = option.dataset.ccValue === value;
        option.classList.toggle('is-active', isActive);
        option.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
    }
  }

  customElements.define('collection-card-dropdown', CollectionCardDropdown);
}
